// Mobile audio unlock utility
// Browsers require a user gesture before audio can play automatically

let audioUnlocked = false;
let unlockPromise: Promise<void> | null = null;

// Silent audio data URL (tiny MP3)
const SILENT_AUDIO = 'data:audio/mp3;base64,SUQzBAAAAAAAI1RTU0UAAAAPAAADTGF2ZjU4Ljc2LjEwMAAAAAAAAAAAAAAA/+M4wAAAAAAAAAAAAEluZm8AAAAPAAAAAwAAAbAAqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqq1dXV1dXV1dXV1dXV1dXV1dXV1dXV1dXV1dXV1dXV1dXV//////////////////////////////////////////////////////////////////8AAAAATGF2YzU4LjEzAAAAAAAAAAAAAAAAJAAAAAAAAAAAAbD/////////AAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAA/+M4wAAFaAG0AEYAKACAHLlKhUqEIQhOKU/BTn1I5HREiQmKUqUp0REQ';

/**
 * Unlock audio playback on mobile browsers.
 * Call this on a user gesture (click, tap) before attempting to play audio.
 */
export const unlockAudio = async (): Promise<void> => {
  if (audioUnlocked) return;
  
  // Prevent multiple concurrent unlock attempts
  if (unlockPromise) return unlockPromise;
  
  unlockPromise = (async () => {
    try {
      let unlocked = false;

      // 1) Unlock WebAudio (iOS Safari uses webkitAudioContext)
      if (typeof window !== "undefined") {
        const AudioContextCtor = (window.AudioContext || (window as any).webkitAudioContext) as
          | typeof AudioContext
          | undefined;

        if (AudioContextCtor) {
          try {
            const ctx = new AudioContextCtor();
            if (ctx.state === "suspended") {
              await ctx.resume();
            }

            // Play a tiny silent buffer to fully unlock on iOS
            const buffer = ctx.createBuffer(1, 1, ctx.sampleRate);
            const source = ctx.createBufferSource();
            source.buffer = buffer;
            source.connect(ctx.destination);
            source.start(0);
            source.stop(0);

            try {
              await ctx.close();
            } catch {
              // ignore
            }

            unlocked = true;
          } catch {
            // Ignore and fall back to HTMLAudio below
          }
        }
      }

      // 2) Unlock HTMLAudio playback
      try {
        const audio = new Audio(SILENT_AUDIO);
        audio.preload = "auto";
        audio.muted = false;
        audio.volume = 0.001; // near-silent but not muted (muted may not unlock on iOS)

        await audio.play();
        audio.pause();
        audio.currentTime = 0;

        unlocked = true;
      } catch {
        // ignore
      }

      if (unlocked) {
        audioUnlocked = true;
        console.log("Audio playback unlocked");
      } else {
        console.warn("Audio unlock failed (will retry on next gesture)");
      }
    } catch (error) {
      console.warn("Audio unlock failed (will retry on next gesture):", error);
      // Don't throw - we'll try again on next gesture
    } finally {
      unlockPromise = null;
    }
  })();
  
  return unlockPromise;
};

/**
 * Check if audio is unlocked
 */
export const isAudioUnlocked = (): boolean => audioUnlocked;

/**
 * Force reset unlock state (for testing)
 */
export const resetAudioUnlock = (): void => {
  audioUnlocked = false;
  unlockPromise = null;
};

/**
 * Play audio with automatic unlock attempt
 * Falls back gracefully if unlock fails
 */
export const playAudioSafe = async (audio: HTMLAudioElement): Promise<void> => {
  try {
    await audio.play();
  } catch (error) {
    if (error instanceof Error && error.name === 'NotAllowedError') {
      // Try to unlock and retry once
      console.warn('Audio play blocked, attempting unlock...');
      await unlockAudio();
      await audio.play();
    } else {
      throw error;
    }
  }
};
